import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import {
  APIResponse,
  Player,
  UserSession,
  PlayerComparisonFilters
} from '../types';
import { DashboardData } from '../types/dashboard';
import { WatchlistPlayer } from '../types/watchlist';
import { ClubContractInput, ClubList, ListAttention, ListSettings, PlayerSummary, RetainCard, PlanResult, PlanStatus, RetainDecision, Scenario, ScenarioMoveInput, Target, TrackerSuggestion, RatingImport, ValuesInfo } from '../types/list';
import { GamedayCompetition, GamedayFixtures, MatchAnalysis } from '../types/gameday';
import { AdminSettings, AdminUsage, AskMessage, AskResult, AskStatus, MatchNarrative } from '../types/ai';
import { LoginResult, PeopleData, Person, PersonInput } from '../types/people';

// Shared cache for API reads. Data only changes when the pipeline runs or a user edits
// something, so reads stay fresh for 5 minutes and any write marks the whole cache stale.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: false,
    },
  },
});

// Writes that only read data, or that happen before any data is cached
const NON_MUTATING_WRITES = ['/auth/validate', '/player_comparison', '/ask'];
// AI scenario planning only suggests moves; adding them is a separate write
const isPlanRequest = (url: string) => /^\/list\/scenarios\/\d+\/plan$/.test(url);

type TokenProvider = (forceRefresh?: boolean) => Promise<string | null>;

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _authRetried?: boolean;
}

class APIClient {
  private client: AxiosInstance;
  private token: string | null = null;
  private tokenProvider: TokenProvider | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseURL: string = import.meta.env.REACT_APP_API_URL || 'http://localhost:3001/api') {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and CSRF header
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        if (config.headers) {
          // Add CSRF protection header for state-changing requests
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          const token = await this.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: refresh the cache after writes, and recover from expired tokens
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        const method = response.config.method?.toLowerCase();
        const url = response.config.url || '';
        if (method && method !== 'get' && !NON_MUTATING_WRITES.some(path => url.startsWith(path)) && !isPlanRequest(url)) {
          queryClient.invalidateQueries();
        }
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as RetryableRequestConfig | undefined;

        // A failed token check is handled by the AuthContext
        if (error.response?.status !== 401 || !config || config.url?.includes('/auth/validate')) {
          return Promise.reject(error);
        }

        // The token may have expired since it was fetched: refresh it once and retry
        if (this.tokenProvider && !config._authRetried) {
          config._authRetried = true;
          const token = await this.tokenProvider(true).catch(() => null);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            return this.client.request(config);
          }
        }

        // Still unauthorized: end the session without reloading the page
        this.clearToken();
        if (this.onUnauthorized) {
          this.onUnauthorized();
        } else {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private async getToken(): Promise<string | null> {
    if (this.tokenProvider) {
      return this.tokenProvider().catch(() => this.token);
    }
    return this.token;
  }

  /**
   * GET through the shared cache: repeat requests within the stale time are served from
   * memory and concurrent identical requests share one network call.
   */
  private async get(url: string, config: { params?: Record<string, any>; timeout?: number } = {}): Promise<{ data: any }> {
    const data = await queryClient.fetchQuery({
      queryKey: [url, config.params ?? {}],
      queryFn: async () => (await this.client.get(url, config)).data,
    });
    return { data };
  }

  /**
   * Fetch a fresh token for every request (Auth0 caches it and refreshes it when it expires)
   */
  setTokenProvider(provider: TokenProvider) {
    this.tokenProvider = provider;
  }

  /**
   * Called when a request is still unauthorized after a token refresh
   */
  setUnauthorizedHandler(handler: () => void) {
    this.onUnauthorized = handler;
  }

  /**
   * Set token in memory only (for Auth0 mode).
   * Token is NOT persisted to storage to prevent XSS token theft.
   */
  setToken(token: string) {
    this.token = token;
    // Security: Do NOT store in localStorage - vulnerable to XSS
    // For dev mode, use setDevToken() which uses sessionStorage
  }

  /**
   * Set token for development mode only.
   * Uses sessionStorage (safer than localStorage - clears on tab close).
   * @param token - Development token
   */
  setDevToken(token: string) {
    this.token = token;
    // sessionStorage is safer than localStorage:
    // - Clears when tab/browser closes
    // - Not synced across tabs
    // - Still accessible to XSS but less persistent exposure
    sessionStorage.setItem('dev_auth_token', token);
  }

  clearToken() {
    this.token = null;
    this.tokenProvider = null;
    queryClient.clear();
    // Clear both possible storage locations
    sessionStorage.removeItem('dev_auth_token');
    // Also clear legacy localStorage if present (migration)
    localStorage.removeItem('auth_token');
  }

  /**
   * Load token from sessionStorage (dev mode only).
   * In production with Auth0, tokens are not persisted.
   */
  loadDevToken(): string | null {
    const storedToken = sessionStorage.getItem('dev_auth_token');
    if (storedToken) {
      this.token = storedToken;
      return storedToken;
    }
    // Check legacy localStorage and migrate (one-time)
    const legacyToken = localStorage.getItem('auth_token');
    if (legacyToken) {
      // Remove from insecure localStorage
      localStorage.removeItem('auth_token');
      // Note: We don't auto-migrate to sessionStorage - require re-auth
    }
    return null;
  }

  // Dashboard
  async getDashboard(): Promise<APIResponse<DashboardData>> {
    const response = await this.get('/dashboard');
    return response.data;
  }

  // Gameday analysis
  async getGamedayCompetitions(): Promise<APIResponse<GamedayCompetition[]>> {
    const response = await this.get('/gameday/competitions');
    return response.data;
  }

  async getGamedayFixtures(competitionId: number, season: number): Promise<APIResponse<GamedayFixtures>> {
    const response = await this.get('/gameday/fixtures', { params: { competition_id: competitionId, season } });
    return response.data;
  }

  // Written by Claude on first request, so allow time for generation
  async getMatchNarrative(gameId: number): Promise<APIResponse<MatchNarrative | null> & { metadata?: { configured: boolean } }> {
    const response = await this.get(`/gameday/match/${gameId}/narrative`, { timeout: 90000 });
    return response.data;
  }

  async getAskStatus(): Promise<APIResponse<AskStatus>> {
    const response = await this.client.get('/ask/status');
    return response.data;
  }

  // Team management: always fresh (other staff may have edited it)
  async getList(view?: string, scenario?: number): Promise<APIResponse<ClubList>> {
    const response = await this.client.get('/list', { params: { view, scenario } });
    return response.data;
  }

  async getScenarios(teamId: number): Promise<APIResponse<Scenario[]>> {
    const response = await this.client.get('/list/scenarios', { params: { team_id: teamId } });
    return response.data;
  }

  async createScenario(scenario: { team_id: number; name: string; shared?: boolean; copy_from?: number }): Promise<APIResponse<{ id: number }>> {
    const response = await this.client.post('/list/scenarios', scenario);
    return response.data;
  }

  async updateScenario(id: number, changes: { name?: string; shared?: boolean }): Promise<APIResponse<unknown>> {
    const response = await this.client.put(`/list/scenarios/${id}`, changes);
    return response.data;
  }

  async deleteScenario(id: number): Promise<APIResponse<unknown>> {
    const response = await this.client.delete(`/list/scenarios/${id}`);
    return response.data;
  }

  async addScenarioMove(id: number, move: ScenarioMoveInput): Promise<APIResponse<{ id: number }>> {
    const response = await this.client.post(`/list/scenarios/${id}/moves`, move);
    return response.data;
  }

  async removeScenarioMove(id: number, moveId: number): Promise<APIResponse<unknown>> {
    const response = await this.client.delete(`/list/scenarios/${id}/moves/${moveId}`);
    return response.data;
  }

  async getPlanStatus(): Promise<APIResponse<PlanStatus>> {
    const response = await this.client.get('/list/plan-status');
    return response.data;
  }

  // Planning can take several AI calls
  async planScenario(id: number, messages: { role: 'user' | 'assistant'; content: string }[]): Promise<APIResponse<PlanResult>> {
    const response = await this.client.post(`/list/scenarios/${id}/plan`, { messages }, { timeout: 190000 });
    return response.data;
  }

  async getListAttention(): Promise<APIResponse<ListAttention>> {
    const response = await this.client.get('/list/attention');
    return response.data;
  }

  async getListSettings(): Promise<APIResponse<ListSettings>> {
    const response = await this.client.get('/list/settings');
    return response.data;
  }

  async updateListSettings(changes: Partial<ListSettings>): Promise<APIResponse<ListSettings>> {
    const response = await this.client.put('/list/settings', changes);
    return response.data;
  }

  async adoptClubList(): Promise<APIResponse<{ copied: number }>> {
    const response = await this.client.post('/list/adopt');
    return response.data;
  }

  async unadoptClubList(): Promise<APIResponse<{ removed: number; kept: number }>> {
    const response = await this.client.post('/list/unadopt');
    return response.data;
  }

  async saveClubContract(contract: ClubContractInput): Promise<APIResponse<unknown>> {
    const response = await this.client.put('/list/contracts', contract);
    return response.data;
  }

  async getTrackerSuggestions(): Promise<APIResponse<TrackerSuggestion[]>> {
    const response = await this.client.get('/list/suggestions');
    return response.data;
  }

  async resolveTrackerSuggestion(id: number, action: 'accept' | 'dismiss'): Promise<APIResponse<unknown>> {
    const response = await this.client.put(`/list/suggestions/${id}`, { action });
    return response.data;
  }

  async getListTargets(params: { season: number; group: string; view?: string; max_age?: number; min_games?: number; min_value?: number; include_unlisted?: boolean })
    : Promise<APIResponse<{ team_id: number; season: number; group: string; targets: Target[]; can_edit: boolean; values?: ValuesInfo }>> {
    const response = await this.client.get('/list/targets', { params, timeout: 30000 });
    return response.data;
  }

  async saveListTarget(target: { team_id: number; season: number; group: string; player_id: number; saved: boolean }): Promise<APIResponse<unknown>> {
    const response = await this.client.put('/list/targets', target);
    return response.data;
  }

  /** The club's own rating of a player; null clears it (recruitment managers and admins) */
  async setRating(playerId: number, value: number | null, note: string | null): Promise<APIResponse<{ player_id: number; value: number | null }>> {
    const response = await this.client.put(`/list/ratings/${playerId}`, { value, note });
    return response.data;
  }

  /** Ratings from a spreadsheet; with save false, only shows what would be saved */
  async importRatings(rows: { player: string; rating: string; note?: string }[], save: boolean): Promise<APIResponse<RatingImport>> {
    const response = await this.client.post('/list/ratings/import', { rows, save });
    return response.data;
  }

  async getRetainCards(view?: string): Promise<APIResponse<{ cards: RetainCard[]; first_season: number; can_edit: boolean; values?: ValuesInfo }>> {
    const response = await this.client.get('/list/retain', { params: { view }, timeout: 30000 });
    return response.data;
  }

  async saveRetainDecision(decision: { team_id: number; player_id: number | null; tracker_name: string; season: number; decision: RetainDecision['decision']; note?: string | null })
    : Promise<APIResponse<unknown>> {
    const response = await this.client.put('/list/retain', decision);
    return response.data;
  }

  async setSalaryBand(band: { team_id: number; player_id: number | null; tracker_name: string; band: string | null }): Promise<APIResponse<unknown>> {
    const response = await this.client.put('/list/salary-band', band);
    return response.data;
  }

  async getPlayerDecisionSummary(playerId: number): Promise<APIResponse<PlayerSummary>> {
    const response = await this.client.get(`/list/player/${playerId}/summary`, { timeout: 30000 });
    return response.data;
  }

  // Organisation administration
  async getAdminSettings(): Promise<APIResponse<AdminSettings>> {
    const response = await this.client.get('/admin/settings');
    return response.data;
  }

  async setAiSwitch(feature: 'ask-data' | 'scenario-ai', enabled: boolean): Promise<APIResponse<AdminSettings['ask_data']>> {
    const response = await this.client.put(`/admin/settings/${feature}`, { enabled });
    return response.data;
  }

  async getPeople(): Promise<APIResponse<PeopleData>> {
    const response = await this.client.get('/admin/people');
    return response.data;
  }

  async addPerson(person: PersonInput): Promise<APIResponse<{ person: Person; login: LoginResult }>> {
    const response = await this.client.post('/admin/people', person);
    return response.data;
  }

  async updatePerson(id: number, person: PersonInput): Promise<APIResponse<{ person: Person }>> {
    const response = await this.client.put(`/admin/people/${id}`, person);
    return response.data;
  }

  async setPersonActive(id: number, active: boolean): Promise<APIResponse<{ person: Person }>> {
    const response = await this.client.post(`/admin/people/${id}/${active ? 'restore' : 'remove'}`);
    return response.data;
  }

  async newPasswordLink(id: number): Promise<APIResponse<{ person: Person; login: LoginResult }>> {
    const response = await this.client.post(`/admin/people/${id}/password-link`);
    return response.data;
  }

  async getAdminUsage(month?: string): Promise<APIResponse<AdminUsage>> {
    const response = await this.client.get('/admin/usage', { params: { month } });
    return response.data;
  }

  // Ask the data: may run several queries, so allow up to two minutes
  async askData(messages: AskMessage[]): Promise<APIResponse<AskResult>> {
    const response = await this.client.post('/ask', { messages }, { timeout: 120000 });
    return response.data;
  }

  async getMatchAnalysis(gameId: number): Promise<APIResponse<MatchAnalysis>> {
    const response = await this.get(`/gameday/match/${gameId}`);
    return response.data;
  }

  async validateToken(token: string): Promise<APIResponse<{ valid: boolean; user: UserSession }>> {
    const response = await this.client.post('/auth/validate', { token });
    return response.data;
  }


  async getPlayerDetail(playerId: number, season?: string): Promise<APIResponse<Player>> {
    const response = await this.get(`/players/${playerId}`, {
      params: { season }
    });
    return response.data;
  }

  // Player statistics table endpoints
  async getPlayerStatistics(params: Record<string, string | string[]>): Promise<APIResponse<any>> {
    const response = await this.get('/players/statistics', { params });
    return response.data;
  }

  async getPlayerStatisticsFilters(): Promise<APIResponse<any>> {
    const response = await this.get('/players/statistics/filters');
    return response.data;
  }

  // Teams endpoints
  async getTeams(): Promise<APIResponse<any>> {
    const response = await this.get('/teams');
    return response.data;
  }

  async getTeamOverview(teamId: number, season?: number): Promise<APIResponse<any>> {
    const response = await this.get(`/teams/${teamId}/overview`, {
      params: season ? { season } : {}
    });
    return response.data;
  }

  async ping(): Promise<APIResponse<any>> {
    const response = await this.get('/health/ping');
    return response.data;
  }

  // Player chart data endpoints
  async getPlayerChartFilters(playerId: number): Promise<APIResponse<any>> {
    const response = await this.get(`/player/${playerId}/available_filters`);
    return response.data;
  }

  async getPlayerPerformanceData(playerId: number, competitionId: number, season: number): Promise<APIResponse<any>> {
    const response = await this.get(`/player/${playerId}/performance_data`, {
      params: { competition_id: competitionId, season: season }
    });
    return response.data;
  }

  async getPlayerSeasonSummaries(playerId: number): Promise<APIResponse<any>> {
    const response = await this.get(`/player/${playerId}/season_summary`);
    return response.data;
  }

  // Scouting Reports endpoints
  async getPlayerScoutingReports(playerId: number, limit?: number): Promise<APIResponse<any>> {
    const response = await this.get(`/scouting_reports/player/${playerId}`, {
      params: { limit: limit || 50 }
    });
    return response.data;
  }

  async createScoutingReport(reportData: {
    player_id: number;
    team_id?: number | null;
    attack_rating: number;
    defensive_rating: number;
    overall_rating: number;
    scouting_notes: string;
  }): Promise<APIResponse<any>> {
    const response = await this.client.post('/scouting_reports', reportData);
    return response.data;
  }

  // Global scouting reports endpoints
  async getScoutingReportsFiltered(params: {
    page?: number;
    limit?: number;
    player_name?: string;
    position?: string;
    scout?: string;
    sort_by?: string;
    sort_direction?: string;
  }): Promise<APIResponse<any>> {
    const response = await this.get('/scouting_reports/filtered', { params });
    return response.data;
  }

  async getScoutingReportsFilterOptions(): Promise<APIResponse<any>> {
    const response = await this.get('/scouting_reports/filter_options');
    return response.data;
  }

  // Player Search endpoint  
  async searchPlayers(query: string, limit?: number): Promise<APIResponse<any>> {
    const response = await this.get('/players/search', {
      params: { q: query, limit: limit || 8 }
    });
    return response.data;
  }

  // Player Game Stats endpoint
  async getPlayerGameStats(
    playerId: number, 
    options: {
      gameId?: number;
      season?: number;
      competitionId?: number;
      limit?: number;
    } = {}
  ): Promise<APIResponse<any>> {
    const response = await this.get(`/player/${playerId}/game_stats`, {
      params: {
        game_id: options.gameId,
        season: options.season,
        competition_id: options.competitionId,
        limit: options.limit || 50
      }
    });
    return response.data;
  }

  // Player Season Stats endpoint
  async getPlayerSeasonStats(
    playerId: number, 
    options: {
      season?: number;
      competitionId?: number;
    } = {}
  ): Promise<APIResponse<any>> {
    const response = await this.get(`/player/${playerId}/season_stats`, {
      params: {
        season: options.season,
        competition_id: options.competitionId
      }
    });
    return response.data;
  }

  // Talent Pipeline endpoints
  async getTalentPipelineData(): Promise<APIResponse<any>> {
    const response = await this.get('/talent_pipeline');
    return response.data;
  }

  async updatePlayerTag(tagData: {
    player_id: number;
    tag_category: string;
    tag: string;
  }): Promise<APIResponse<any>> {
    // Validate player_id before sending request
    if (tagData.player_id === undefined || tagData.player_id === null || isNaN(tagData.player_id)) {
      console.error('updatePlayerTag called with invalid player_id:', tagData);
      return { success: false, message: 'Invalid player ID', data: null };
    }
    const response = await this.client.put('/talent_pipeline/player_tag', tagData);
    return response.data;
  }

  async removePlayerTag(tagData: {
    player_id: number;
    tag_category: string;
  }): Promise<APIResponse<any>> {
    // Validate player_id before sending request
    if (tagData.player_id === undefined || tagData.player_id === null || isNaN(tagData.player_id)) {
      console.error('removePlayerTag called with invalid player_id:', tagData);
      return { success: false, message: 'Invalid player ID', data: null };
    }
    const response = await this.client.delete('/talent_pipeline/player_tag', { data: tagData });
    return response.data;
  }

  async getWatchlist(): Promise<APIResponse<WatchlistPlayer[]>> {
    const response = await this.get('/talent_pipeline/watchlist');
    return response.data;
  }

  async addToWatchlist(playerId: number): Promise<APIResponse<{ player_id: number; watching: boolean }>> {
    const response = await this.client.put(`/talent_pipeline/watchlist/${playerId}`);
    return response.data;
  }

  async removeFromWatchlist(playerId: number): Promise<APIResponse<{ player_id: number; watching: boolean }>> {
    const response = await this.client.delete(`/talent_pipeline/watchlist/${playerId}`);
    return response.data;
  }

  async getPlayerTalentStatus(playerId: number): Promise<APIResponse<any>> {
    const response = await this.get(`/talent_pipeline/players/${playerId}`);
    return response.data;
  }

  // Player Comparison endpoints
  async getPlayerComparisonFilterOptions(): Promise<APIResponse<any>> {
    const response = await this.get('/player_comparison');
    return response.data;
  }

  async searchPlayersForComparison(query: string, limit?: number): Promise<APIResponse<any>> {
    const response = await this.get('/player_comparison/search_players', {
      params: { q: query, limit: limit || 10 }
    });
    return response.data;
  }

  async getPlayerComparison(filters: PlayerComparisonFilters): Promise<APIResponse<any>> {
    const response = await this.client.post('/player_comparison', filters);
    return response.data;
  }

  // Team player performance (heatmap)
  async getTeamPlayerPerformance(
    teamId: number,
    competitionId?: number,
    season?: number
  ): Promise<APIResponse<any>> {
    const response = await this.get(`/teams/${teamId}/player-performance`, {
      params: {
        competition_id: competitionId,
        season
      }
    });
    return response.data;
  }

  // Game Reports endpoints
  async getGames(options?: { competitionId?: number; season?: number; limit?: number }): Promise<APIResponse<any>> {
    const response = await this.get('/game_reports/games', {
      params: {
        competition_id: options?.competitionId,
        season: options?.season,
        limit: options?.limit
      }
    });
    return response.data;
  }

  async getGameTeamReviews(gameId: number, teamId: number): Promise<APIResponse<any>> {
    const response = await this.get(`/game_reports/${gameId}/team/${teamId}`);
    return response.data;
  }

  async submitBatchReviews(
    gameId: number,
    teamId: number,
    reviews: Array<{
      player_id: number;
      attack: number | null;
      defence: number | null;
      overall: number | null;
      comment: string;
    }>
  ): Promise<APIResponse<any>> {
    const response = await this.client.post(`/game_reports/${gameId}/team/${teamId}`, { reviews });
    return response.data;
  }

  async getPlayerCoachReports(playerId: number): Promise<APIResponse<{
    reports: Array<{
      round: number;
      season: number;
      competition_id: number;
      competition_name: string;
      opponent: string;
      author: string;
      attack: number;
      defence: number;
      overall: number;
      comment: string;
      created_at: string;
    }>;
    averages: {
      attack: number;
      defence: number;
      overall: number;
    };
  }>> {
    const response = await this.get(`/game_reports/player/${playerId}/coach-reports`);
    return response.data;
  }
}

// Create and export singleton instance
export const apiClient = new APIClient();

// Note: Token loading is now handled by AuthContext
// - Auth0 mode: tokens are managed by Auth0 SDK, not persisted
// - Dev mode: AuthContext calls loadDevToken() when needed

export default apiClient;